import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import './App.css';

// Auth
import Login from './pages/Auth/Login';

// API
import { callPublicApi } from './api';

// New Components
import BottomNav from './components/BottomNav';
import MobileHeader from './components/MobileHeader';

// Assets
import siteLogo from './assets/logo.png';

// Pages
import Dashboard from './pages/Dashboard';
import HaberYonetim from './pages/HaberYonetim';
import EtkinlikYonetim from './pages/EtkinlikYonetim';
import UyeYonetim from './pages/UyeYonetim';
import Ayarlar from './pages/Ayarlar';
import Analiz from './pages/Analiz';
import TarifYonetim from './pages/TarifYonetim';
import BasvuruYonetim from './pages/BasvuruYonetim';
import GaleriYonetim from './pages/GaleriYonetim';
import QrOkuyucu from './pages/QrOkuyucu';
import SiteYonetim from './pages/SiteYonetim';
import KullaniciYonetim from './pages/KullaniciYonetim';
import AuditLogs from './pages/AuditLogs';
import HataLoglari from './pages/HataLoglari';
import MedyaYonetim from './pages/Medya/MedyaYonetim';
import AkademiYonetim from './pages/AkademiYonetim';
import SystemSettings from './pages/SystemSettings';
import TabakDenetim from './pages/TabakDenetim';

// Protected Route Wrapper
const ProtectedRoute = ({ children, isAuthenticated }) => {
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

// Yetkilendirme Listesi (Süper Adminler)
const ADMIN_EMAILS = [
  'steamdeneme38@gmail.com',
  'ggassh123@gmail.com',
  'admin',
  'gastro_super',
  'muzik@klu.edu.tr'
];

// Page name map for MobileHeader
const PAGE_NAMES = {
  '/': 'Dashboard',
  '/qr-okuyucu': 'QR Yoklama',
  '/analiz': 'Raporlar',
  '/haberler': 'Haberler',
  '/etkinlikler': 'Etkinlikler',
  '/basvurular': 'Başvurular',
  '/tarifler': 'Tarifler',
  '/akademi': 'Gastro Akademi',
  '/galeri': 'Galeri',
  '/medya': 'Medya',
  '/uyeler': 'Üyeler',
  '/site-yonetim': 'Site Yönetimi',
  '/ayarlar': 'Ayarlar',
  '/kullanicilar': 'Kullanıcılar',
  '/denetim': 'Denetim',
  '/hata-loglari': 'Hata Logları',
  '/settings/system': 'Sistem',
  '/tabak-denetim': 'Tabak Denetim'
};

// Inner layout (needs useLocation inside Router)
function AppLayout({ user, kulup, isAuthenticated, isSuperAdmin, isMainClub, hasPerm, handleLogout, handleClubChange }) {
  const location = useLocation();

  // Determine current page name
  const pageName = PAGE_NAMES[location.pathname] || 'KluGastro';

  // Determine FAB action based on current page
  const getFabAction = () => {
    const path = location.pathname;
    // Pages where FAB adds new items — handled by existing page logic
    const fabPages = ['/haberler', '/etkinlikler', '/tarifler', '/uyeler'];
    if (fabPages.includes(path)) {
      return path;
    }
    return null;
  };

  const fabPage = getFabAction();

  return (
    <div className="admin-layout">
      {/* === MOBILE HEADER === */}
      <MobileHeader kulup={kulup} pageName={pageName} />

      {/* === DESKTOP SIDEBAR (always visible on desktop, hidden on mobile via CSS) === */}
      <nav className={`sidebar ${kulup.kulupID}`}>
        {/* Club Selector (SuperAdmin only) */}
        {isSuperAdmin && (
          <div className="club-selector">
            <label>Yönetilen Kulüp</label>
            <select value={kulup.kulupID} onChange={handleClubChange}>
              <option value="klugastro">Gastronomi 🍳</option>
              <option value="muzik">Müzik 🎵</option>
              <option value="spor">Spor ⚽</option>
            </select>
          </div>
        )}

        {/* Logo & Name */}
        <div className="sidebar-header">
          <img src={siteLogo} alt="Logo" style={{ objectFit: 'contain', background: 'transparent', borderColor: 'transparent' }} />
          <h3>{kulup?.ad || 'KLUGASTRONOMİ'}</h3>
        </div>

        <p className="user-email">
          {user?.username || 'Kullanıcı'}
        </p>

        {/* Navigation */}
        <ul>
          <li>
            <Link to="/" className={location.pathname === '/' ? 'active' : ''}>
              <span className="nav-icon">📊</span> Dashboard
            </Link>
          </li>

          {hasPerm('qr-okuyucu') && (
            <li>
              <Link to="/qr-okuyucu" className={location.pathname === '/qr-okuyucu' ? 'active' : ''}>
                <span className="nav-icon">📱</span> QR Yoklama
              </Link>
            </li>
          )}

          {hasPerm('analiz') && (
            <li>
              <Link to="/analiz" className={location.pathname === '/analiz' ? 'active' : ''}>
                <span className="nav-icon">📈</span> Raporlar
              </Link>
            </li>
          )}

          {hasPerm('haberler') && isMainClub && (
            <li>
              <Link to="/haberler" className={location.pathname === '/haberler' ? 'active' : ''}>
                <span className="nav-icon">📰</span> Haberler
              </Link>
            </li>
          )}

          {hasPerm('etkinlikler') && (
            <li>
              <Link to="/etkinlikler" className={location.pathname === '/etkinlikler' ? 'active' : ''}>
                <span className="nav-icon">🎪</span> Etkinlikler
              </Link>
            </li>
          )}

          {hasPerm('basvurular') && (
            <li>
              <Link to="/basvurular" className={location.pathname === '/basvurular' ? 'active' : ''}>
                <span className="nav-icon">💰</span> Başvurular
              </Link>
            </li>
          )}

          {hasPerm('tarifler') && isMainClub && (
            <li>
              <Link to="/tarifler" className={location.pathname === '/tarifler' ? 'active' : ''}>
                <span className="nav-icon">🍽️</span> Tarifler
              </Link>
            </li>
          )}

          {hasPerm('tarifler') && isMainClub && (
            <li>
              <Link to="/akademi" className={location.pathname === '/akademi' ? 'active' : ''}>
                <span className="nav-icon">🎓</span> Gastro Akademi
              </Link>
            </li>
          )}

          {hasPerm('galeri') && isMainClub && (
            <li>
              <Link to="/galeri" className={location.pathname === '/galeri' ? 'active' : ''}>
                <span className="nav-icon">🖼️</span> Galeri
              </Link>
            </li>
          )}

          {hasPerm('galeri') && isMainClub && (
            <li>
              <Link to="/medya" className={location.pathname === '/medya' ? 'active' : ''}>
                <span className="nav-icon">📸</span> Medya
              </Link>
            </li>
          )}

          {hasPerm('galeri') && isMainClub && (
            <li>
              <Link to="/tabak-denetim" className={location.pathname === '/tabak-denetim' ? 'active' : ''}>
                <span className="nav-icon">🍽️</span> Tabak Denetim
              </Link>
            </li>
          )}

          {hasPerm('uyeler') && (
            <li>
              <Link to="/uyeler" className={location.pathname === '/uyeler' ? 'active' : ''}>
                <span className="nav-icon">👥</span> Üyeler
              </Link>
            </li>
          )}

          {hasPerm('site-yonetim') && (
            <li>
              <Link to="/site-yonetim" className={location.pathname === '/site-yonetim' ? 'active' : ''}>
                <span className="nav-icon">🌐</span> Site Yönetimi
              </Link>
            </li>
          )}

          {hasPerm('ayarlar') && (
            <li>
              <Link to="/ayarlar" className={location.pathname === '/ayarlar' ? 'active' : ''}>
                <span className="nav-icon">⚙️</span> Ayarlar
              </Link>
            </li>
          )}

          {(hasPerm('kullanicilar') || hasPerm('denetim')) && (
            <>
              <li className="nav-divider">
                <span className="nav-section-label">Yönetim</span>
              </li>
              {hasPerm('kullanicilar') && (
                  <li>
                    <Link to="/kullanicilar" className={location.pathname === '/kullanicilar' ? 'active' : ''}>
                      <span className="nav-icon">👤</span> Kullanıcılar
                    </Link>
                  </li>
              )}
              {hasPerm('denetim') && (
                  <li>
                    <Link to="/settings/system" className={location.pathname === '/settings/system' ? 'active' : ''}>
                      <span className="nav-icon">⚙️</span> Sistem Ayarları
                    </Link>
                  </li>
              )}
              {hasPerm('denetim') && (
                  <li>
                    <Link to="/denetim" className={location.pathname === '/denetim' ? 'active' : ''}>
                      <span className="nav-icon">🛡️</span> Denetim
                    </Link>
                  </li>
              )}
              {hasPerm('denetim') && (
                  <li>
                    <Link to="/hata-loglari" className={location.pathname === '/hata-loglari' ? 'active' : ''}>
                      <span className="nav-icon">🐞</span> Hata Logları
                    </Link>
                  </li>
              )}
            </>
          )}
        </ul>

        <button onClick={handleLogout} className="signout-button">
          🚪 Çıkış Yap
        </button>
      </nav>

      {/* === MAIN CONTENT === */}
      <main className="content">
        <Routes>
          <Route path="/" element={<Dashboard kulup={kulup} />} />
          <Route path="/qr-okuyucu" element={<QrOkuyucu />} />
          <Route path="/analiz" element={<Analiz />} />
          <Route path="/haberler" element={isMainClub ? <HaberYonetim kulup={kulup} /> : <Navigate to="/" replace />} />
          <Route path="/etkinlikler" element={<EtkinlikYonetim kulup={kulup} />} />
          <Route path="/basvurular" element={<BasvuruYonetim kulup={kulup} />} />
          <Route path="/tarifler" element={isMainClub ? <TarifYonetim kulup={kulup} /> : <Navigate to="/" replace />} />
          <Route path="/akademi" element={isMainClub ? <AkademiYonetim /> : <Navigate to="/" replace />} />
          <Route path="/galeri" element={isMainClub ? <GaleriYonetim kulup={kulup} /> : <Navigate to="/" replace />} />
          <Route path="/medya" element={isMainClub ? <MedyaYonetim /> : <Navigate to="/" replace />} />
          <Route path="/tabak-denetim" element={isMainClub ? <TabakDenetim /> : <Navigate to="/" replace />} />
          <Route path="/uyeler" element={<UyeYonetim />} />
          <Route path="/site-yonetim" element={<SiteYonetim kulup={kulup} />} />
          <Route path="/ayarlar" element={<Ayarlar hasPerm={hasPerm} isSuperAdmin={isSuperAdmin} user={user} />} />
          <Route path="/kullanicilar" element={<KullaniciYonetim />} />
          <Route path="/denetim" element={user && (user.role === 'superadmin' || user.username === 'admin') ? <AuditLogs /> : <Navigate to="/" />} />
          <Route path="/hata-loglari" element={user && (user.role === 'superadmin' || user.username === 'admin' || (user.permissions && user.permissions.includes('denetim'))) ? <HataLoglari /> : <Navigate to="/" />} />
          <Route path="/settings/system" element={isSuperAdmin ? <SystemSettings /> : <Navigate to="/" />} />
          <Route path="*" element={<h2 className="page-title">404 | Sayfa Bulunamadı</h2>} />
        </Routes>
      </main>

      {/* === MOBILE FAB (only on certain pages) === */}
      {fabPage && (
        <button
          className="fab-button"
          onClick={() => {
            // Trigger "Yeni Ekle" on the current page
            // Each page component listens for its own add button
            const addBtn = document.querySelector('.yeni-ekle-button');
            if (addBtn) addBtn.click();
          }}
          aria-label="Yeni Ekle"
        >
          +
        </button>
      )}

      {/* === MOBILE BOTTOM NAV === */}
      <BottomNav hasPerm={hasPerm} isSuperAdmin={isSuperAdmin} user={user} />
    </div>
  );
}

function App() {
  console.log("ADMIN APP VERSION: 3.0.0 (UI Redesign)");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Club State
  const [kulup, setKulup] = useState({
    kulupID: 'klugastro',
    ad: 'Klu Gastronomi',
    kisaAd: 'KluGastro',
    ozellikler: {
      haberler: true,
      etkinlikler: true,
      galeri: true,
      tarifler: true,
      uyeler: true
    }
  });

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('user');

    if (token) {
      setIsAuthenticated(true);
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
        } catch (e) {
          console.error("User Parse Error:", e);
          localStorage.removeItem('user');
        }
      }
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData) => {
    setIsAuthenticated(true);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);
  };

  // Auth & Permissions
  const userIdentifier = (user?.username || '').toLowerCase().trim();
  const isSuperAdmin = ADMIN_EMAILS.includes(userIdentifier) || user?.role === 'superadmin' || user?.username?.toLowerCase() === 'gastro_super';
  const isMainClub = kulup.kulupID === 'klugastro';

  const hasPerm = (permKey) => {
    if (isSuperAdmin) return true;
    return user?.permissions?.includes(permKey);
  };

  // Club loading effect
  useEffect(() => {
    if (userIdentifier && isAuthenticated) {
      const storedClubID = localStorage.getItem('selectedClubID');
      let targetClubID = storedClubID || 'klugastro';

      if (!isSuperAdmin) {
        if (userIdentifier.includes('muzik')) {
          targetClubID = 'muzik';
        } else if (userIdentifier.includes('spor')) {
          targetClubID = 'spor';
        } else {
          targetClubID = user.kulupID || 'klugastro';
        }
      }

      setKulup(prev => ({
        ...prev,
        kulupID: targetClubID,
        ad: targetClubID.toUpperCase() + ' KULÜBÜ'
      }));

      localStorage.setItem('selectedClubID', targetClubID);

      const fetchFullClubData = async () => {
        try {
          const response = await callPublicApi(`/kulupler?id=${targetClubID}`);
          let fullData = null;
          if (response && response.item) fullData = response.item;
          else if (response && response.items && response.items.length > 0) fullData = response.items[0];

          if (fullData) {
            setKulup(prev => ({ ...prev, ...fullData }));
          }
        } catch (err) {
          console.error("Kulüp detayları yüklenemedi:", err);
        }
      };
      fetchFullClubData();
    }
  }, [userIdentifier, isSuperAdmin, isAuthenticated, user]);

  const handleClubChange = (e) => {
    const newID = e.target.value;
    const clubNameMap = {
      'klugastro': 'Klu Gastronomi',
      'muzik': 'Müzik Kulübü',
      'spor': 'Spor Kulübü'
    };

    setKulup(prev => ({
      ...prev,
      kulupID: newID,
      ad: clubNameMap[newID]
    }));
    localStorage.setItem('selectedClubID', newID);
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Yükleniyor...</p>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        {/* LOGIN */}
        <Route path="/login" element={
          isAuthenticated ? <Navigate to="/" replace /> : <Login onLogin={handleLogin} />
        } />

        {/* PROTECTED ROUTES */}
        <Route path="/*" element={
          <ProtectedRoute isAuthenticated={isAuthenticated}>
            <AppLayout
              user={user}
              kulup={kulup}
              isAuthenticated={isAuthenticated}
              isSuperAdmin={isSuperAdmin}
              isMainClub={isMainClub}
              hasPerm={hasPerm}
              handleLogout={handleLogout}
              handleClubChange={handleClubChange}
            />
          </ProtectedRoute>
        } />
      </Routes>
    </Router>
  );
}

export default App;